import React from "react";
import {
  HStack,
  IconButton,
  Text,
  TextProps,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { ViewControl } from "../ViewControl/ViewControl";
import { DeleteIcon } from "../../styles/icons";

type FilterSummaryVariant = "default" | "compact";

type FilterSummaryProps = {
  queryString: string;
  variant?: FilterSummaryVariant;
  /** When provided, tags become removable and call back with the updated query string. */
  onQueryChange?: (queryString: string) => void;
};

const formatLabel = (label: string, variant: FilterSummaryVariant) => {
  const formatted = label.replace(/([a-z])([A-Z])/g, "$1 $2");
  return variant === "compact"
    ? formatted.replace(/\b\w/g, (c) => c.toUpperCase())
    : formatted;
};

const displayValueToRaw = (displayValue: string) => {
  if (displayValue === "Yes") return "true";
  if (displayValue === "No") return "false";
  return displayValue;
};

const parseQuery = (queryString: string) => {
  const params = new URLSearchParams(queryString);
  const summary: Record<string, { include: string[]; exclude: string[] }> = {};

  for (const [key, value] of Array.from(params.entries()) as [
    string,
    string
  ][]) {
    const targetKey = key.trim();
    const isExclusion = value.startsWith("!");
    let actualValue = isExclusion ? value.slice(1) : value;

    if (!summary[targetKey]) summary[targetKey] = { include: [], exclude: [] };

    if (isExclusion) {
      summary[targetKey].exclude.push(actualValue);
    } else if (actualValue) {
      actualValue =
        actualValue === "true"
          ? "Yes"
          : actualValue === "false"
          ? "No"
          : actualValue;
      summary[targetKey].include.push(actualValue);
    }
  }

  return summary;
};

export const removeFilterValueFromQuery = (
  queryString: string,
  key: string,
  displayValue: string,
  isExclusion: boolean
): string => {
  const rawValue = displayValueToRaw(displayValue);
  const valueToRemove = isExclusion ? `!${rawValue}` : rawValue;
  // Read via URLSearchParams (decodes), but rebuild with the same unencoded
  // `?key=value&...` format used by MemberFilterQueryForm — toString() would
  // encode spaces/`!` and break Edit Filter dropdown matching.
  const params = new URLSearchParams(queryString);
  const pairs: Array<[string, string]> = [];
  let removed = false;

  for (const [paramKey, paramValue] of Array.from(params.entries())) {
    if (!removed && paramKey.trim() === key && paramValue === valueToRemove) {
      removed = true;
      continue;
    }
    pairs.push([paramKey, paramValue]);
  }

  if (pairs.length === 0) return "";
  return `?${pairs.map(([k, v]) => `${k}=${v}`).join("&")}`;
};

const FilterTag: React.FC<{
  label: string;
  onRemove?: () => void;
}> = ({ label, onRemove }) => (
  <HStack
    as="span"
    display="inline-flex"
    alignItems="center"
    bg="cherryBackground.tag"
    border="1px solid"
    borderColor="cherryBorder.100"
    borderRadius="full"
    pl={3}
    pr={onRemove ? 1 : 3}
    py={1}
    spacing={2}
  >
    <Text
      fontSize="sm"
      color="cherryGrey.1100"
      lineHeight="1.2"
      whiteSpace="nowrap"
    >
      {label}
    </Text>
    {onRemove && (
      <IconButton
        aria-label={`Remove ${label}`}
        icon={<DeleteIcon boxSize={2.5} />}
        size="xs"
        minW="18px"
        h="18px"
        p={0}
        borderRadius="full"
        bg="white"
        border="1px solid"
        borderColor="cherryBorder.100"
        color="cherryGrey.1100"
        onClick={onRemove}
        _hover={{ bg: "gray.50" }}
        _active={{ bg: "gray.100" }}
      />
    )}
  </HStack>
);

const renderTags = (values: string[], onRemove?: (value: string) => void) => (
  <Wrap spacing={2}>
    {values.map((val, idx) => (
      <WrapItem key={`${val}-${idx}`}>
        <FilterTag
          label={val}
          onRemove={onRemove ? () => onRemove(val) : undefined}
        />
      </WrapItem>
    ))}
  </Wrap>
);

const COMPACT_LABEL_WIDTH = "180px";
const DEFAULT_LABEL_WIDTH = "220px";

const filterLabelProps = (variant: FilterSummaryVariant): TextProps =>
  variant === "compact"
    ? {
        flex: `0 0 ${COMPACT_LABEL_WIDTH}`,
        w: COMPACT_LABEL_WIDTH,
        color: "unset",
        textTransform: "unset",
      }
    : {
        flex: `0 0 ${DEFAULT_LABEL_WIDTH}`,
        w: DEFAULT_LABEL_WIDTH,
      };

const filterValueProps: TextProps = {
  as: "div",
  flex: 1,
  minW: 0,
  w: "100%",
};

const renderValues = (
  title: string,
  values: string[],
  variant: FilterSummaryVariant,
  onRemove?: (value: string) => void
) =>
  values.length > 0 ? (
    <ViewControl
      label={formatLabel(title, variant)}
      labelProps={filterLabelProps(variant)}
      valueProps={filterValueProps}
      value={renderTags(values, onRemove)}
    />
  ) : null;

const renderExclusions = (
  values: string[],
  variant: FilterSummaryVariant,
  onRemove?: (value: string) => void
) =>
  values.length > 0 && (
    <ViewControl
      label={"Excluding:"}
      labelProps={
        variant === "compact"
          ? {
              flex: `0 0 ${COMPACT_LABEL_WIDTH}`,
              w: COMPACT_LABEL_WIDTH,
              textTransform: "unset",
            }
          : {
              flex: `0 0 ${DEFAULT_LABEL_WIDTH}`,
              w: DEFAULT_LABEL_WIDTH,
            }
      }
      valueProps={filterValueProps}
      value={renderTags(values, onRemove)}
      mt={"4px !important"}
    />
  );

export const MembershipFilterSummary: React.FC<FilterSummaryProps> = ({
  queryString,
  variant = "default",
  onQueryChange,
}) => {
  const summary = parseQuery(queryString);
  const hasFilters = Object.values(summary).some(
    ({ include, exclude }) => include.length > 0 || exclude.length > 0
  );

  const spacing = variant === "compact" ? 3 : 6;
  const isRemovable = !!onQueryChange;

  const handleRemove = (
    key: string,
    displayValue: string,
    isExclusion: boolean
  ) => {
    if (!onQueryChange) return;
    onQueryChange(
      removeFilterValueFromQuery(queryString, key, displayValue, isExclusion)
    );
  };

  return (
    <VStack align="start" w="100%" spacing={spacing}>
      {hasFilters ? (
        Object.entries(summary).map(([key, { include, exclude }]) =>
          include.length > 0 || exclude.length > 0 ? (
            <React.Fragment key={key}>
              {renderValues(
                key + ":",
                include,
                variant,
                isRemovable
                  ? (value) => handleRemove(key, value, false)
                  : undefined
              )}
              {renderExclusions(
                exclude,
                variant,
                isRemovable
                  ? (value) => handleRemove(key, value, true)
                  : undefined
              )}
            </React.Fragment>
          ) : null
        )
      ) : (
        <Text>No filters configured</Text>
      )}
    </VStack>
  );
};
